import { useState } from "react";
import Slider from "react-slick";
import {
  Box,
  IconButton,
  Skeleton,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const StyledArrowButton = styled(IconButton)({
  display: "none",
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 1,
  backgroundColor: "var(--neutral-white)",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  "&:hover": {
    backgroundColor: "var(--neutral-white)",
  },
  "& .MuiSvgIcon-root": {
    color: "var(--neutral-black)",
    fontSize: "20px",
  },
});

function NextArrow({ onClick }) {
  return (
    <StyledArrowButton onClick={onClick} sx={{ right: "10px" }}>
      <ArrowForwardIosIcon />
    </StyledArrowButton>
  );
}

function PrevArrow({ onClick }) {
  return (
    <StyledArrowButton onClick={onClick} sx={{ left: "10px" }}>
      <ArrowBackIosNewIcon />
    </StyledArrowButton>
  );
}

function Banner({ items, isLoading, onBannerClick }) {
  const theme = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);

  if (items?.length === 0) return null;

  const settings = {
    dots: true,
    infinite: true,
    autoplay: true,
    fade: true,
    autoplaySpeed: 4000,
    speed: 1200,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (current, next) => setCurrentSlide(next),
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 1, slidesToScroll: 1 } },
      { breakpoint: 768, settings: { slidesToShow: 1, slidesToScroll: 1 } },
      { breakpoint: 480, settings: { slidesToShow: 1, slidesToScroll: 1 } },
    ],
    customPaging: (i) => (
      <Box
        sx={{
          width: i === currentSlide ? "16px" : "8px",
          height: "8px",
          borderRadius: i === currentSlide ? "4px" : "50%",
          backgroundColor:
            i === currentSlide
              ? "var(--primary-red-base)"
              : "var(--neutral-300)",
          margin: "0 4px",
          transition: "all 0.5s ease",
          marginTop: "12px",
        }}
      />
    ),
  };

  return (
    <Box
      sx={{
        position: "relative",
        p: 0,
        mx: "auto",
        width: { xs: "100%", md: "90%", lg: "88%" },
        maxWidth: "1380px",
        borderRadius: { xs: "20px", md: "30px" },
        overflow: "hidden",
        boxShadow: "0 18px 42px rgba(23, 18, 12, 0.06)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.80) 0%, rgba(243,236,223,0.75) 100%)",
        "& *:focus": { outline: "none" },
        "& *:focus-visible": { outline: "none" },
      }}
    >
      {isLoading ? (
        <Skeleton
          variant="rectangular"
          sx={{
            height: { xs: "160px", sm: "180px", md: "420px" },
            borderRadius: { xs: "18px", md: "26px" },
          }}
        />
      ) : (
        <Slider {...settings}>
          {items?.map((banner, index) => (
            <Box
              key={index}
              onClick={() => {
                if (
                  !banner?.route_type ||
                  !banner?.route_item ||
                  !banner?.route_data
                ) {
                  console.log("No navigation data in banner");
                  return;
                }
                onBannerClick?.(banner);
              }}
              sx={{
                p: 0,
                borderRadius: { xs: "18px", md: "28px" },
                overflow: "hidden",
                background: "linear-gradient(135deg, #fffdf9 0%, #f7f1e7 100%)",
                boxShadow: "0 18px 42px rgba(19, 25, 22, 0.06)",
                border: "1px solid rgba(215, 169, 74, 0.18)",
                transition: "transform 0.35s ease, box-shadow 0.35s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 22px 50px rgba(19, 25, 22, 0.10)",
                },
                "& img": {
                  width: "100%",
                  display: "block",
                  height: { xs: "220px", sm: "260px", md: "430px" },
                  objectFit: "cover",
                  transition: "transform 0.8s ease",
                  animation: "gentle-float 10s ease-in-out infinite alternate",
                },
                cursor: "pointer",
              }}
            >
              <img src={banner.web_image} alt={`Banner ${index}`} />
            </Box>
          ))}
        </Slider>
      )}
    </Box>
  );
}

export default Banner;
